import React, {CSSProperties, MouseEvent, useCallback, useEffect, useRef, useState} from "react"
import classNames from "classnames";

import styles from '../css/Input.module.css'
import Icon from "./Icon";
import icons from "../css/Icon.module.css";
import Button from "./Button";
import Box from "./Box";
import {setCaretPosition} from "../utils/CSSUtils";

const ENTER = 13;
const BACKSPACE = 8;
const LEFT = 37;
const UP = 38;
const RIGHT = 39;
const DOWN = 40;

export interface Props {
    id: string,
    xs?: boolean,
    onChange?: (value: string) => void,
    bottom?: boolean,
    shrink?: boolean,
    color?: string,
    value?: string,
    isPreview?: boolean,
    onChangeColorButtonPress?: (event: MouseEvent) => void,
    placeholder?: string,
    allowChangingColor?: boolean,
    maxLength?: number,
}

export default (props: Props) => {

    // Props

    const {id, maxLength = 32, isPreview, color, onChangeColorButtonPress, bottom, shrink, xs, value, onChange, placeholder, allowChangingColor = true} = props;

    // Refs

    const inputRef = useRef<HTMLDivElement>(null);

    // State

    const [isFocus, setIsFocus] = useState(false);
    const [isButtonFocus, setIsButtonFocus] = useState(false);

    // Effects

    useEffect(() => {

        if (inputRef.current) {

            let pos = 0;
            if (isFocus) {
                // @ts-ignore
                let _range = document.getSelection().getRangeAt(0);
                let range = _range.cloneRange();
                range.selectNodeContents(inputRef.current);
                range.setEnd(_range.endContainer, _range.endOffset);
                pos = range.toString().length;
            }

            const textContent = inputRef.current.textContent;
            inputRef.current.textContent = textContent + ' ';
            inputRef.current.textContent = textContent;

            if (isFocus) {
                setCaretPosition(inputRef.current, pos);
            }

        }

    }, [isFocus]);

    // Callbacks

    const handleOnChange = useCallback(
        () => {
            if (!onChange || !inputRef || !inputRef.current) return;
            onChange(inputRef.current.textContent || '');
        }, [onChange]
    );

    const handleOnFocus = useCallback(
        (isFocus) => {
            !isButtonFocus && setIsFocus(isFocus);
            !isFocus && handleOnChange();
        }, [isButtonFocus, handleOnChange]
    );

    const handleOnPaste = useCallback(
        (e) => {
            e.preventDefault();

            const currentText = inputRef && inputRef.current && inputRef.current.textContent ?
                inputRef.current.textContent : '';

            let text = e.clipboardData.getData("text");
            text = text.trim().replace(/(\r\n|\n|\r)/gm, ' ');

            if (text.length + currentText.length > maxLength) {
                return
            }
            document.execCommand('insertText', false, text);

        }, [maxLength]
    );

    let style: CSSProperties = {};
    if (color) style = {...style, color};
    if (isPreview) style = {...style, backgroundColor: 'transparent'};

    return (
        <Box center bottom={bottom} shrink={shrink}>

            <div>
                {
                    isFocus && !isPreview && allowChangingColor &&
                    <div className={styles.advanceTextInputBadge}>
                        <Button xs primary
                                onClick={(e) => {
                                    setIsFocus(false);
                                    setIsButtonFocus(false);
                                    onChangeColorButtonPress && onChangeColorButtonPress(e);
                                }}
                                onMouseOver={setIsButtonFocus.bind(null, true)}
                                onMouseOut={setIsButtonFocus.bind(null, false)}>
                            <Icon xs white name={icons.iconBrush}/>
                        </Button>
                    </div>
                }

                <div
                    id={id}
                    ref={inputRef}
                    style={style}
                    onKeyDown={(e) => {
                        e.keyCode === ENTER && e.preventDefault(); // prevent enter

                        // Prevent max length on key down
                        const isAllowedKeyCode =
                            e.keyCode === BACKSPACE ||
                            e.keyCode === LEFT ||
                            e.keyCode === UP ||
                            e.keyCode === RIGHT ||
                            e.keyCode === DOWN ||
                            e.ctrlKey ||
                            e.metaKey;

                        // @ts-ignore
                        const isTextSelected = window.getSelection().focusOffset !== window.getSelection().anchorOffset;

                        !isAllowedKeyCode && inputRef && inputRef.current && inputRef.current.innerHTML.length >= maxLength && !isTextSelected && e.preventDefault()
                    }}
                    onPaste={handleOnPaste}
                    contentEditable={true}
                    spellCheck={isFocus}
                    onFocus={handleOnFocus.bind(null, true)}
                    onBlur={handleOnFocus.bind(null, false)}
                    className={classNames(styles.advanceTextInput, {
                        [styles.advanceTextInputXs]: xs,
                        [styles.advanceTextInputPreview]: isPreview
                    })}
                    dangerouslySetInnerHTML={{__html: value || ''}}
                    placeholder={placeholder}
                />
            </div>

        </Box>
    )
}
